import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { RoomState, SafeMessage, SafeRoom } from "ghost-server";
import { useSub } from "@/hooks/useSub";
import { useUserId } from "@/hooks/useUserId";
import { useRoomState } from "@/hooks/useRoomState";
import { api } from "@/lib/api";
import { formatSecondsAsDuration } from "@/lib/duration";

type RoomSummary = {
	id: string;
	name: string;
	createdAt: Date;
	ghostedAt: Date | null;
	lastMessageAt: Date | null;
	isVIPRoom: boolean;
	userCount?: number;
	stateOverrides?: { state: string; transitionAfterSeconds: number }[];
};

type ChatLimits = {
	messageWindowAllowance: number;
	messageWindowDurationMs: number;
	messageMaxContentLength: number;
};

function SidebarRoomRow({ room, states, isCurrent }: { room: RoomSummary; states: RoomState[]; isCurrent: boolean }) {
	const navigate = useNavigate();
	const roomState = useRoomState(room, states);
	const stateKey = roomState?.current?.state ?? "";
	const isGhosted = stateKey === "ghost" || room.ghostedAt != null;
	const isWarning =
		!!roomState &&
		stateKey !== "default" &&
		stateKey !== "ghost" &&
		roomState.elapsedSeconds != null &&
		roomState.current !== null &&
		roomState.elapsedSeconds >= roomState.current.transitionAfterSeconds;
	const stateObj = states.find((s) => s.state === stateKey);
	const rowTextColor = stateObj?.color ?? "#DCDCDC";

	return (
		<button
			onClick={() => navigate(`/room/${room.id}`)}
			style={{ color: rowTextColor }}
			className={`w-full flex items-center justify-between px-5 py-5 border-b border-white text-sm tracking-wide transition-colors text-left cursor-pointer
				${isCurrent ? "bg-[#DCDCDC10]" : ""}`}
		>
			<span className="min-w-0 flex items-center">
				<span className="block truncate max-w-[140px] min-[1200px]:max-w-[170px]">
					{room.isVIPRoom && <span className="text-[#ffcc00] mr-1">★</span>}
					{room.name}
				</span>
				<span className="ml-1.5 shrink-0 font-tomorrow-bold leading-[130%] tracking-[20%]">
					[{room.userCount ?? 0}]
				</span>
			</span>
			<span className="ml-2 shrink-0 flex items-center gap-1.5">
				{!isGhosted && isWarning && stateObj && (
					<span style={{ color: stateObj.color ?? undefined }}>
						<img
							src={
								stateKey === "critical" || stateKey === "termination" ? "/images/Exclaimation.svg" : "/images/Bell.svg"
							}
							alt={stateKey === "critical" || stateKey === "termination" ? "alert" : "warning"}
							className="w-4 h-4"
							style={{ filter: stateObj.color ? `drop-shadow(0 0 2px ${stateObj.color})` : undefined }}
						/>
					</span>
				)}
				{isGhosted && (
					<span style={{ color: rowTextColor }} className="text-xs">
						👻
					</span>
				)}
			</span>
		</button>
	);
}

export function Room() {
	const { roomId } = useParams<{ roomId: string }>();
	const navigate = useNavigate();
	const [messages, setMessages] = useState<SafeMessage[]>([]);
	const [room, setRoom] = useState<SafeRoom>();
	const [states, setStates] = useState<RoomState[]>([]);
	const [rooms, setRooms] = useState<RoomSummary[]>([]);
	const [messageError, setMessageError] = useState<string | null>(null);
	const [messageRetryUntilMs, setMessageRetryUntilMs] = useState<number | null>(null);
	const [nowMs, setNowMs] = useState(() => Date.now());
	const [chatLimits, setChatLimits] = useState<ChatLimits>({
		messageWindowAllowance: 4,
		messageWindowDurationMs: 10_000,
		messageMaxContentLength: 200,
	});
	const [msgContent, setMsgContent] = useState("");
	const [isMobileInfoOpen, setIsMobileInfoOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [createRoomName, setCreateRoomName] = useState("");
	const [isCreatingRoom, setIsCreatingRoom] = useState(false);
	const [createRoomError, setCreateRoomError] = useState<string | null>(null);
	const userId = useUserId();
	const sub = useSub(roomId!, userId);
	const roomForState = useMemo(() => {
		if (!room) return undefined;

		const latestMessageAt = messages.reduce<Date | string | null>((latest, message) => {
			if (!latest || new Date(message.createdAt).getTime() > new Date(latest).getTime()) return message.createdAt;
			return latest;
		}, null);

		return { ...room, lastMessageAt: latestMessageAt ?? room.lastMessageAt };
	}, [room, messages]);
	const roomState = useRoomState(roomForState, states);
	const bottomRef = useRef<HTMLDivElement>(null);
	const isGhost = room?.ghostedAt != null || roomState?.current?.state === "ghost";
	const currentRoomUserCount = rooms.find((r) => r.id === roomId)?.userCount ?? 0;
	const activeDuration = formatSecondsAsDuration(roomState?.activeElapsedSeconds ?? 0);
	const messageRetrySecondsLeft =
		messageRetryUntilMs == null ? null : Math.max(0, Math.ceil((messageRetryUntilMs - nowMs) / 1000));
	const activeMessageError =
		messageRetrySecondsLeft && messageRetrySecondsLeft > 0
			? `Too many messages. Wait ${messageRetrySecondsLeft} second${messageRetrySecondsLeft === 1 ? "" : "s"} and try again.`
			: messageError;

	async function refreshSidebarData() {
		const { data } = await api.chat.all.get();
		if (!data) return;
		setStates((data as any).states ?? []);
		setRooms((data as any).rooms ?? []);
	}

	async function refreshChatLimits() {
		const { data } = await api.chat.limits.get();
		if (!data) return;
		setChatLimits({
			messageWindowAllowance: (data as any).messageWindowAllowance,
			messageWindowDurationMs: (data as any).messageWindowDurationMs,
			messageMaxContentLength: (data as any).messageMaxContentLength,
		});
	}

	useEffect(() => {
		void refreshSidebarData();
		void refreshChatLimits();
		const interval = setInterval(() => void refreshSidebarData(), 5000);
		return () => clearInterval(interval);
	}, [roomId]);

	useEffect(() => {
		const listener = sub.on("message", (msg: any) => {
			let data: any;
			try {
				data = typeof msg.data === "string" ? JSON.parse(msg.data) : msg.data;
			} catch {
				return;
			}

			if (!data || typeof data !== "object") return;

			if (Array.isArray(data.messages)) {
				setMessages(data.messages);
				setRoom(data);
				setTimeout(() => void refreshSidebarData(), 300);
			} else if (data.type === "send-error" && typeof data.message === "string") {
				if (data.code === "rate-limited" && typeof data.retryAfterMs === "number") {
					setMessageRetryUntilMs(Date.now() + data.retryAfterMs);
					setMessageError(null);
				} else {
					setMessageRetryUntilMs(null);
					setMessageError(data.message);
				}
			} else if (data.type === "messages-deleted" && Array.isArray(data.messageIds)) {
				const deletedIds = new Set(data.messageIds);
				setMessages((prev) => prev.filter((message) => !deletedIds.has(message.id)));
			} else if (data.type === "message-updated" && data.message) {
				setMessages((prev) =>
					prev.map((message) => (message.id === data.message.id ? (data.message as SafeMessage) : message)),
				);
			} else if (data.content) {
				setMessages((prev) => [...prev, data as SafeMessage]);
			}
		});
		return () => {
			listener.close();
		};
	}, [sub]);

	useEffect(() => {
		if (messageRetryUntilMs == null) return;

		const interval = setInterval(() => {
			const now = Date.now();
			setNowMs(now);
			if (messageRetryUntilMs <= now) {
				setMessageRetryUntilMs(null);
			}
		}, 250);

		return () => clearInterval(interval);
	}, [messageRetryUntilMs]);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	useEffect(() => {
		setIsMobileInfoOpen(false);
	}, [roomId]);

	function sendMessage() {
		if (!msgContent.trim()) return;
		if (isGhost) {
			setMessageRetryUntilMs(null);
			setMessageError("Room is ghosted. No new transmissions are allowed.");
			return;
		}

		const trimmedMessage = msgContent.trim();
		if (trimmedMessage.length > chatLimits.messageMaxContentLength) {
			setMessageRetryUntilMs(null);
			setMessageError(`Message is too long. Max ${chatLimits.messageMaxContentLength} characters.`);
			return;
		}

		if (messageRetryUntilMs != null && messageRetryUntilMs > Date.now()) {
			return;
		}

		setMessageRetryUntilMs(null);
		setMessageError(null);
		sub.send({ userId, messageContent: msgContent });
		setMsgContent("");
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	}

	async function handleCreateRoom(e: React.FormEvent) {
		e.preventDefault();
		if (isCreatingRoom) return;

		setCreateRoomError(null);
		setIsCreatingRoom(true);

		const trimmedName = createRoomName.trim();
		const payload = trimmedName.length > 0 ? { userId, name: trimmedName } : { userId };
		const { data, error } = await api.chat.new.post(payload);
		setIsCreatingRoom(false);

		if (error) {
			const status = (error as any)?.status;
			const value = (error as any)?.value;
			if (status === 409) {
				setCreateRoomError("Room name already exists. Please choose another.");
			} else if (status === 429) {
				setCreateRoomError("Too many room creations. Please wait and try again.");
			} else if (status === 400 && value === "Please use a proper title!") {
				setCreateRoomError("Please use a proper title!");
			} else {
				setCreateRoomError("Failed to create room. Please try again.");
			}
			return;
		}

		if (!data) {
			setCreateRoomError("Failed to create room. Please try again.");
			return;
		}

		setIsCreateModalOpen(false);
		setCreateRoomName("");
		await refreshSidebarData();
		navigate(`/room/${data.id}`);
	}

	return (
		<div className="relative h-dvh w-full" style={{ background: "#81818130" }}>
			{/* Background image */}
			<div className="absolute inset-0 pointer-events-none z-0">
				<img
					src="/images/Home2.png"
					alt=""
					style={{ opacity: 0.5, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
				/>
			</div>

			{/* Main layout */}
			<div className="absolute inset-0 z-10 flex min-w-0 items-stretch">
				{/* Left: chat */}
				<div className="min-w-0 flex-1 min-h-0 p-0 min-[1000px]:p-[24px] min-[1000px]:pr-[25px]">
					<div className="min-w-0 h-full min-h-0 flex flex-col overflow-hidden border border-white bg-black">
						{/* Header */}
						<div className="flex min-w-0 items-center justify-between gap-3 p-[24px] border-b border-white shrink-0 min-h-[72px] bg-[#1F1F1F] text-white overflow-hidden">
							<div className="min-w-0 flex-1 flex items-center gap-2 pr-2">
								<button
									onClick={() => navigate("/rooms")}
									className="group cursor-pointer hover:text-white transition-colors text-sm tracking-widest flex items-center gap-2"
								>
									<img src="/images/left-arrow.svg" alt="back" className="w-4 h-4" />
								</button>
								<span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap uppercase font-tomorrow-bold leading-[130%] tracking-[20%] min-[1000px]:max-w-[420px]">
									{room?.name ?? "ROOM"}
								</span>
							</div>

							<div className="hidden min-[1000px]:flex items-center gap-4 text-sm tracking-[20%] font-tomorrow-light">
								<span className="flex gap-2 items-center mr-[44px]">
									<img src="/images/User.svg" alt="back" className="w-5 h-5" />
									Members:
									<strong className="text-white">{currentRoomUserCount}</strong>
								</span>
								{!isGhost && (
									<span className="flex gap-2">
										<img src="/images/Clock.svg" alt="back" className="w-5 h-5" /> Active:{" "}
										<strong className="text-white">{activeDuration}</strong>
									</span>
								)}
								{/* {shouldShowStatus && <span style={{ color: stateObjCurrent?.color }}>[{stateObjCurrent?.state}]</span>} */}
							</div>

							<button
								type="button"
								onClick={() => setIsMobileInfoOpen((prev) => !prev)}
								aria-label="Toggle room info"
								className="min-[1000px]:hidden shrink-0"
							>
								<img src="/images/mobile-info-icon.svg" alt="info" className="cursor-pointer w-6 h-6" />
							</button>
						</div>

						{isMobileInfoOpen && (
							<div className="min-[1000px]:hidden min-w-0 bg-black text-white border-b border-dashed border-white/50 overflow-hidden">
								<div className="px-6 py-[34px] text-center border-b border-dashed border-white/50">
									<h2 className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-tomorrow-bold text-[16px] tracking-[20%] leading-[130%] uppercase">
										{room?.name ?? "ROOM NAME"}
									</h2>
								</div>
								<div className="px-6 py-6 flex flex-col gap-5 text-[16px] tracking-[20%] leading-[130%] font-tomorrow-light">
									<span className="flex items-center justify-center gap-3">
										<img src="/images/User.svg" alt="members" className="w-5 h-5" />
										<span>
											Members: <strong className="font-tomorrow-bold text-white">{currentRoomUserCount}</strong>
										</span>
									</span>
									{!isGhost && (
										<span className="flex items-center justify-center gap-3">
											<img src="/images/Clock.svg" alt="active" className="w-5 h-5" />
											<span>
												Active: <strong className="font-tomorrow-bold text-white">{activeDuration}</strong>
											</span>
										</span>
									)}
									{/* {shouldShowStatus && <span style={{ color: stateInfo.color }}>[{stateInfo.label}]</span>} */}
								</div>
							</div>
						)}

						{/* Messages */}
						<div className="flex-1 min-h-0 min-w-0 overflow-y-auto chat-scrollbar px-4 py-4 flex flex-col gap-0">
							{isGhost && (
								<p className="text-center text-white/30 text-xs tracking-widest py-6">— THIS ROOM HAS BEEN GHOSTED —</p>
							)}
							{!isGhost && messages.length === 0 && (
								<p className="text-center text-white/40 text-sm tracking-[20%] py-8 uppercase font-tomorrow-light">
									No transmissions yet
								</p>
							)}
							{!isGhost &&
								messages.map((msg) => {
									const isSelf = msg.author === userId;
									const messageStyle = msg.isBlocked
										? { borderColor: "#ff990044", color: "#ffb84d" }
										: { borderColor: isSelf ? "#ffffff22" : "#ffffff22", color: "white" };
									return (
										<div
											key={msg.id}
											className={`flex flex-col min-[1000px]:flex-row py-5 border-b border-dashed border-white/20 gap-3 min-[1000px]:gap-4 ${isSelf ? "min-[1000px]:flex-row-reverse" : ""}`}
										>
											<div
												className={`shrink-0 font-tomorrow-light text-white leading-[24px] tracking-[20%] flex items-center gap-3 ${isSelf ? "justify-end text-right" : "justify-start text-left"} min-[1000px]:block`}
											>
												{isSelf ? (
													<>
														<div>
															{new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
														</div>
														<div className="font-tomorrow-bold leading-[24px] tracking-[20%] text-white">{"<you>"}</div>
													</>
												) : (
													<>
														<div className="font-tomorrow-bold leading-[24px] tracking-[20%] text-white">{`<${msg.author}>`}</div>
														<div>
															{new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
														</div>
													</>
												)}
											</div>
											<div
												className={`min-w-0 break-words [overflow-wrap:anywhere] leading-[24px] tracking-[20%] font-tomorrow-light ${isSelf ? "text-right" : "text-left"}`}
												style={messageStyle}
											>
												{msg.content}
											</div>
										</div>
									);
								})}
							<div ref={bottomRef} />
						</div>

						{/* Input */}
						<div className="px-4 py-3 border-t border-white shrink-0 bg-[#1F1F1F] p-[24px]">
							{isGhost ? (
								<p className="text-center text-white/30 text-xs tracking-widest py-[10px] px-[20px]">
									[ROOM GHOSTED — NO NEW TRANSMISSIONS]
								</p>
							) : (
								<>
									<div className="flex items-center gap-3">
										<input
											type="text"
											value={msgContent}
											onChange={(e) => {
												setMsgContent(e.target.value);
												if (messageError) setMessageError(null);
											}}
											onKeyDown={handleKeyDown}
											placeholder="Start Typing"
											className="flex-1 outline-none text-white placeholder:text-white/30 tracking-[20%] leading-[24px] py-[10px] px-[20px] bg-black"
										/>
										<button
											onClick={sendMessage}
											disabled={!msgContent.trim() || (messageRetryUntilMs != null && messageRetryUntilMs > Date.now())}
											className="disabled:cursor-not-allowed transition-colors leading-[24px] tracking-[20%] bg-white cursor-pointer p-[10px] flex text-black items-center gap-[10px]"
										>
											<img src="/images/left-arrow-black.svg" alt="send" className="w-4 h-4" />
											{" Send"}
										</button>
									</div>
									{activeMessageError && (
										<p className="mt-3 text-[11px] tracking-wide text-[#f5b942]">{activeMessageError}</p>
									)}
								</>
							)}
						</div>
					</div>
				</div>

				{/* Right: sidebar */}
				<div className="hidden min-[1000px]:flex w-[319px] bg-[#1F1F1F] h-full shrink-0 flex-col overflow-hidden border-l border-white">
					<div className="font-tomorrow-medium p-5 border-b border-white text-sm tracking-widest text-white leading-[24px] tracking-[20%]">
						All Rooms:
					</div>
					<div className="flex-1 overflow-y-auto sidebar-scrollbar">
						{rooms.map((r) => (
							<SidebarRoomRow key={r.id} room={r} states={states} isCurrent={r.id === roomId} />
						))}
					</div>
					<div className="flex flex-col items-center justify-center border-t border-white px-5 mt-[-1px]">
						<button
							onClick={() => {
								setCreateRoomError(null);
								setCreateRoomName("");
								setIsCreateModalOpen(true);
							}}
							className="w-full mx-5 my-[18px] py-[10px] bg-black border border-white text-white leading-[24px] cursor-pointer tracking-[20%] border-t"
						>
							Add Room
						</button>
					</div>
				</div>
			</div>

			{isCreateModalOpen && (
				<div
					className="absolute inset-0 z-30 bg-black/70 flex items-center justify-center p-4"
					onClick={() => setIsCreateModalOpen(false)}
				>
					<div className="w-full max-w-137.5 border border-white bg-black" onClick={(e) => e.stopPropagation()}>
						<div className="h-16 px-7 border-b border-white bg-[#1F1F1F] text-white flex items-center justify-between">
							<div className="flex items-center gap-3">
								<button
									onClick={() => setIsCreateModalOpen(false)}
									className="group cursor-pointer hover:text-white transition-colors text-sm tracking-widest flex items-center gap-2"
								>
									<img src="/images/left-arrow.svg" alt="back" className="w-4 h-4" />
								</button>
								<h2 className="uppercase font-tomorrow-bold leading-[130%] tracking-[20%]">Name The Room</h2>
							</div>
							<button
								onClick={() => setIsCreateModalOpen(false)}
								aria-label="Close"
								className="text-white/80 hover:text-white text-xl leading-none cursor-pointer"
							>
								X
							</button>
						</div>

						<form
							onSubmit={handleCreateRoom}
							className="min-h-65 px-6 min-[1000px]:px-10 py-10 min-[1000px]:py-16 flex items-center justify-center"
						>
							<div className="w-full max-w-92.5 flex flex-col gap-3 min-[1000px]:gap-5">
								<div className="w-full flex flex-col min-[640px]:flex-row items-stretch min-[640px]:items-center gap-3 min-[1000px]:gap-5">
									<input
										type="text"
										value={createRoomName}
										onChange={(e) => setCreateRoomName(e.target.value)}
										placeholder="Name..."
										className="flex-1 h-10 px-5 bg-[#1F1F1F] border border-white/60 outline-none text-white placeholder:text-white/60 font-tomorrow-light tracking-[20%]"
									/>
									<button
										type="submit"
										disabled={isCreatingRoom}
										className="w-full min-[640px]:w-auto h-10 px-7 bg-white text-black border border-white leading-6 tracking-[20%] font-tomorrow-light cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{isCreatingRoom ? "Creating..." : "Create"}
									</button>
								</div>
								{createRoomError && <p className="text-[11px] tracking-wide text-[#f5b942]">{createRoomError}</p>}
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}
